import { ref, onMounted, onUnmounted } from 'vue'

export function useGeolocation() {
	const coordinates = ref({})
	const error = ref(null)
	const granted = ref(false)
	const supported = ref('geolocation' in navigator)
	let watchId = null
	let lastUpdate = 0
	const MIN_UPDATE_INTERVAL = 10000

	const ErrorTypes = {
		PERMISSION_DENIED: 'PERMISSION_DENIED',
		POSITION_UNAVAILABLE: 'POSITION_UNAVAILABLE',
		TIMEOUT: 'TIMEOUT',
		UNSUPPORTED: 'UNSUPPORTED',
		UNKNOWN: 'UNKNOWN'
	}

	const setNoCoordinates = (message = 'No hay coordenadas disponibles', type = ErrorTypes.UNKNOWN) => {
		coordinates.value = {}
		error.value = {
			type,
			message,
			timestamp: Date.now()
		}
	}

	const checkPermissionStatus = async () => {
		if (!supported.value) {
			setNoCoordinates('Geolocalización no soportada en este navegador', ErrorTypes.UNSUPPORTED)
			return false
		}

		try {
			// Verificar si la API de permisos está disponible
			if (navigator.permissions && navigator.permissions.query) {
				try {
					const result = await navigator.permissions.query({ name: 'geolocation' })
					granted.value = result.state === 'granted'

					// Escuchar cambios en el estado del permiso
					result.addEventListener('change', () => {
						granted.value = result.state === 'granted'
						if (!granted.value) {
							stopWatching()
							setNoCoordinates('Permiso de geolocalización denegado', ErrorTypes.PERMISSION_DENIED)
						} else {
							// Capturar la promesa para evitar UnhandledRejection
							startWatching().catch(err => {
								console.error('Error al reiniciar geolocalización tras cambio de permisos:', err)
								setNoCoordinates('Error al reiniciar geolocalización', ErrorTypes.UNKNOWN)
							})
						}
					})

					if (!granted.value) {
						setNoCoordinates('Permiso de geolocalización no otorgado', ErrorTypes.PERMISSION_DENIED)
					}
					return granted.value
				} catch (permError) {
					console.warn('Error al consultar permisos:', permError)
					// Si falla la consulta de permisos, intentar continuar de todas formas
					return true
				}
			}
		} catch (e) {
			console.warn('Error al verificar permisos:', e)
			setNoCoordinates('Error al verificar permisos de geolocalización', ErrorTypes.UNKNOWN)
		}
		// Si no hay API de permisos, asumir que podemos intentar obtener la ubicación
		return true
	}

	const updatePosition = position => {
		const currentTime = Date.now()

		if (currentTime - lastUpdate < MIN_UPDATE_INTERVAL) {
			return
		}

		error.value = null
		granted.value = true
		const { latitude, longitude } = position.coords
		coordinates.value = { originLat: latitude, originLng: longitude }
		lastUpdate = currentTime
	}

	const handleError = geoError => {
		console.warn('Error obteniendo la ubicación:', geoError.message)

		let errorType
		switch (geoError.code) {
			case 1:
				errorType = ErrorTypes.PERMISSION_DENIED
				granted.value = false
				break
			case 2:
				errorType = ErrorTypes.POSITION_UNAVAILABLE
				break
			case 3:
				errorType = ErrorTypes.TIMEOUT
				break
			default:
				errorType = ErrorTypes.UNKNOWN
		}

		setNoCoordinates(geoError.message, errorType)
	}

	const startWatching = async () => {
		if (supported.value) {
			try {
				const hasPermission = await checkPermissionStatus()
				if (!hasPermission && watchId !== null) {
					stopWatching()
					return
				}

				watchId = navigator.geolocation.watchPosition(updatePosition, handleError, {
					enableHighAccuracy: true,
					timeout: 10000,
					maximumAge: 5000 // Permitir usar cache de 5 segundos
				})
			} catch (err) {
				console.error('Error al iniciar seguimiento de geolocalización:', err)
				setNoCoordinates('Error al iniciar geolocalización', ErrorTypes.UNKNOWN)
			}
		} else {
			console.error('Geolocalización no soportada en este navegador.')
			setNoCoordinates('Geolocalización no soportada en este navegador', ErrorTypes.UNSUPPORTED)
		}
	}

	const stopWatching = () => {
		if (watchId !== null) {
			navigator.geolocation.clearWatch(watchId)
			watchId = null
		}
	}

	onMounted(() => {
		startWatching().catch(err => {
			console.error('Error al iniciar geolocalización en onMounted:', err)
			setNoCoordinates('Error al iniciar geolocalización', ErrorTypes.UNKNOWN)
		})
	})
	onUnmounted(stopWatching)

	return {
		coordinates,
		error,
		granted,
		supported,
		ErrorTypes
	}
}
